import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { EUR, fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { DASH } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, AlertTriangle, CalendarClock, Users, HandCoins,
  Target, BellRing, School, Banknote, ClipboardList, HeartHandshake, Send,
  Settings as SettingsIcon, ArrowRight, Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth, isAdminOrSegreteria } from "@/lib/auth";
import { canAccessModule } from "@/lib/permissions";
import { TicketAlert } from "@/components/helpdesk/TicketAlert";

const AREA_TILES = [
  { moduleKey: "school",         to: "/scuola",          label: "Scuola",         desc: "Studenti, corsi, esami, presenze",   icon: School,         color: "#1C352D", testId: "dash-area-scuola" },
  { moduleKey: "administration", to: "/amministrazione", label: "Amministrazione", desc: "Entrate, uscite, casse, report",     icon: Banknote,       color: "#B4632F", testId: "dash-area-admin" },
  { moduleKey: "mansioni",       to: "/mansioni",        label: "Mansioni",       desc: "Turni, planner, impegni esterni",     icon: ClipboardList,  color: "#556B2F", testId: "dash-area-mansioni" },
  { moduleKey: "fundraising",    to: "/donatori",        label: "Raccolta fondi", desc: "Donatori, donazioni, scadenze",      icon: HeartHandshake, color: "#E2725B", testId: "dash-area-fundraising" },
  { moduleKey: "wallet",         to: "/wallet",          label: "Wallet",         desc: "Credito virtuale studenti e docenti", icon: Wallet,        color: "#0F766E", testId: "dash-area-wallet" },
  { moduleKey: "segreteria",     to: "/richieste",       label: "Segreteria",     desc: "Richieste, messaggi, avvisi",         icon: Send,           color: "#4A6572", testId: "dash-area-segreteria" },
  { moduleKey: "settings",       to: "/impostazioni",    label: "Impostazioni",   desc: "Utenti, scuola, configurazioni",     icon: SettingsIcon,   color: "#6B4E71", testId: "dash-area-settings" },
];

function AreaTile({ to, label, desc, icon: Icon, color, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-stone-300"
    >
      <div
        className="w-11 h-11 rounded-xl grid place-items-center text-white transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: color }}
      >
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="text-base font-semibold tracking-tight text-stone-900">{label}</div>
        <ArrowRight size={16} className="text-stone-300 group-hover:text-stone-600 group-hover:translate-x-0.5 transition-all"/>
      </div>
      <div className="text-xs text-stone-500 mt-1 leading-relaxed">{desc}</div>
      <div
        className="absolute -bottom-8 -right-8 w-24 h-24 rounded-full opacity-[0.04] group-hover:opacity-[0.08] transition-opacity"
        style={{ backgroundColor: color }}
      />
    </Link>
  );
}

function Kpi({ testId, title, value, hint, icon: Icon, tone = "default", subvalue }) {
  const tones = {
    default: "bg-white",
    accent: "bg-[#1C352D] text-stone-50",
    warn: "bg-orange-50 border-orange-200",
    danger: "bg-red-50 border-red-200",
  };
  const iconTones = {
    default: "text-[#1C352D] bg-[#1C352D]/8",
    accent: "text-[#E2725B] bg-white/10",
    warn: "text-orange-700 bg-orange-100",
    danger: "text-red-700 bg-red-100",
  };
  return (
    <Card
      data-testid={testId}
      className={`h-full p-6 border border-stone-200 rounded-xl shadow-none transition-transform duration-200 hover:-translate-y-0.5 ${tones[tone]}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg grid place-items-center ${iconTones[tone]}`}>
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div className="text-[10px] uppercase tracking-widest opacity-70">{title}</div>
      </div>
      <div className="mt-6">
        <div className="text-3xl font-semibold tracking-tight">{value}</div>
        {subvalue && <div className="text-xs mt-1 opacity-70">{subvalue}</div>}
        {hint && <div className="text-xs mt-3 opacity-80">{hint}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const canManage = isAdminOrSegreteria(user);
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  const { data: smart } = useQuery({
    queryKey: ["smart-lists"],
    queryFn: async () => (await api.get("/smart-lists")).data,
  });

  const { data: upcoming } = useQuery({
    queryKey: ["schedules-upcoming"],
    queryFn: async () => (await api.get("/schedules", { params: { status: "attesa" } })).data,
  });

  if (isLoading || !data) {
    return <div className="p-10 text-stone-500 text-sm">Caricamento dashboard…</div>;
  }

  const growthUp = (data.growth_percent || 0) >= 0;
  const upcomingList = (upcoming || []).slice(0, 6);
  const lateList = smart?.in_ritardo?.slice(0, 5) || [];
  const thankList = smart?.da_ringraziare?.slice(0, 5) || [];

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Buon lavoro."
        subtitle="Riepilogo raccolta, ritardi e priorità operative di oggi."
      />
      <TicketAlert />

      {/* Hub aree — visibile solo per admin/segreteria, mostra scorciatoie a tutti i moduli */}
      {canManage && (
        <section className="mb-8" data-testid="dash-areas-hub">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-3">Aree</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
            {AREA_TILES.filter((t) => canAccessModule(user, t.moduleKey)).map((t) => <AreaTile key={t.to} {...t} />)}
          </div>
        </section>
      )}

      {/* Top row: primary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        <Kpi
          testId={DASH.kpiMonth}
          title="Raccolta del mese"
          value={EUR.format(data.month_total || 0)}
          subvalue={`${data.month_count} donazioni`}
          hint={
            <span className={`inline-flex items-center gap-1 ${growthUp ? "text-emerald-700" : "text-red-700"}`}>
              {growthUp ? <TrendingUp size={13}/> : <TrendingDown size={13}/>}
              {isFinite(data.growth_percent) ? `${data.growth_percent.toFixed(1)}%` : "n/d"} vs mese precedente
            </span>
          }
          icon={HandCoins}
          tone="accent"
        />
        <Kpi
          testId={DASH.kpiYear}
          title="Raccolta totale"
          value={EUR.format(data.all_time_total || 0)}
          subvalue={`${data.all_time_count || 0} donazioni · anno ${data.reference_year}: ${EUR.format(data.year_total || 0)}`}
          hint={<span>Promesso: {EUR.format(data.promised_ytd || 0)}</span>}
          icon={Target}
        />
        <Kpi
          testId={DASH.kpiPromised}
          title="Tasso incasso"
          value={`${(data.collection_rate || 0).toFixed(0)}%`}
          subvalue="Ricevuto / promesso YTD"
          icon={TrendingUp}
        />
        <Kpi
          testId={DASH.kpiLate}
          title="Scadenze in ritardo"
          value={data.late_schedules}
          subvalue={`${data.upcoming_30d} in scadenza (30 gg)`}
          icon={AlertTriangle}
          tone={data.late_schedules > 0 ? "danger" : "default"}
        />
      </div>

      {/* Second row: sub-metrics + chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 mt-5">
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none h-full">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Andamento 12 mesi</div>
            <div className="text-xs text-stone-500 mono">Serie storica</div>
          </div>
          <div className="h-56 mt-2" data-testid={DASH.chartMonthly}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_series || []} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7d9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6c655a" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6c655a" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v)} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #e7e2d3", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => EUR.format(v)}
                />
                <Line type="monotone" dataKey="total" stroke="#1C352D" strokeWidth={2} dot={{ r: 3, fill: "#E2725B", stroke: "#1C352D", strokeWidth: 1 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none h-full">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Priorità di oggi</div>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between" data-testid={DASH.kpiFollowups}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 grid place-items-center"><BellRing size={16}/></div>
                <div>
                  <div className="text-sm font-medium">Follow-up da fare</div>
                  <div className="text-xs text-stone-500">Reminders aperti</div>
                </div>
              </div>
              <div className="text-2xl font-semibold">{data.followups_today}</div>
            </div>
            <div className="flex items-center justify-between" data-testid={DASH.kpiActiveDonors}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center"><Users size={16}/></div>
                <div>
                  <div className="text-sm font-medium">Donatori totali</div>
                  <div className="text-xs text-stone-500">Chiese {data.by_type?.chiesa || 0} · Aziende {data.by_type?.azienda || 0} · Privati {data.by_type?.privato || 0}</div>
                </div>
              </div>
              <div className="text-2xl font-semibold">{data.total_donors}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 grid place-items-center"><AlertTriangle size={16}/></div>
                <div>
                  <div className="text-sm font-medium">Donatori in ritardo</div>
                  <div className="text-xs text-stone-500">Da contattare oggi</div>
                </div>
              </div>
              <div className="text-2xl font-semibold">{lateList.length}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none h-full">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Top donatori YTD</div>
            <Link to="/report" className="text-[11px] text-[#E2725B] hover:underline">Vedi report</Link>
          </div>
          <div className="mt-4 space-y-3">
            {(data.top_donors || []).map((t, i) => (
              <div key={t.donor_id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-6 h-6 rounded-md bg-[#1C352D] text-white text-[11px] grid place-items-center">{i+1}</div>
                  <div className="truncate">
                    <div className="truncate font-medium">{t.name}</div>
                    <div className="text-[11px] text-stone-500">{t.type}</div>
                  </div>
                </div>
                <div className="font-semibold">{EUR.format(t.total)}</div>
              </div>
            ))}
            {(data.top_donors || []).length === 0 && (
              <div className="text-xs text-stone-500">Nessuna donazione registrata quest'anno.</div>
            )}
          </div>
        </Card>
      </div>

      {/* Third row: operational lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 mt-5">
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium flex items-center gap-2"><CalendarClock size={15}/> Prossime scadenze</div>
            <Link to="/scadenze" className="text-[11px] text-[#E2725B] hover:underline">Tutte</Link>
          </div>
          <ul className="divide-y divide-stone-100">
            {upcomingList.map((s) => (
              <li key={s.id} className="py-2 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{s.donor_name}</div>
                  <div className="text-[11px] text-stone-500">Scade {fmtDate(s.due_date)}</div>
                </div>
                <div className="font-semibold">{EUR.format(s.amount)}</div>
              </li>
            ))}
            {upcomingList.length === 0 && <li className="py-3 text-xs text-stone-500">Nessuna scadenza attesa.</li>}
          </ul>
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium flex items-center gap-2 text-red-800"><AlertTriangle size={15}/> Donatori in ritardo</div>
            <Link to="/followup" className="text-[11px] text-[#E2725B] hover:underline">Follow-up</Link>
          </div>
          <ul className="divide-y divide-stone-100">
            {lateList.map((d) => (
              <li key={d.id} className="py-2 text-sm">
                <Link to={`/donatori/${d.id}`} className="hover:underline font-medium">
                  {d.org_name || `${d.first_name || ""} ${d.last_name || ""}`}
                </Link>
                <div className="text-[11px] text-stone-500">Ultimo pagamento: {fmtDate(d.last_payment_date)}</div>
              </li>
            ))}
            {lateList.length === 0 && <li className="py-3 text-xs text-stone-500">Nessun donatore in ritardo. Ottimo lavoro!</li>}
          </ul>
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium flex items-center gap-2 text-emerald-800"><HandCoins size={15}/> Da ringraziare</div>
            <Link to="/followup" className="text-[11px] text-[#E2725B] hover:underline">Apri lista</Link>
          </div>
          <ul className="divide-y divide-stone-100">
            {thankList.map((d) => (
              <li key={d.id} className="py-2 text-sm">
                <Link to={`/donatori/${d.id}`} className="hover:underline font-medium">
                  {d.org_name || `${d.first_name || ""} ${d.last_name || ""}`}
                </Link>
                <div className="text-[11px] text-stone-500">Donazione recente · {fmtDate(d.last_payment_date)}</div>
              </li>
            ))}
            {thankList.length === 0 && <li className="py-3 text-xs text-stone-500">Nessuna donazione recente in attesa di ringraziamento.</li>}
          </ul>
        </Card>
      </div>
    </div>
  );
}
